import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { message } from 'antd';
import { fetchTestData } from '../services/api';
import {
  ArrowRightOutlined,
  UploadOutlined,
  FileAddOutlined,
  LineChartOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

const dmaic = [
  {
    phase: 'Define / Measure',
    modules: [
      { name: '基本统计', desc: '描述统计·正态检验·分布拟合' },
      { name: 'MSA', desc: 'GR&R·偏倚·线性·稳定性' },
      { name: '过程能力', desc: 'Cp·Cpk·Pp·Ppk·PPM' },
    ],
  },
  {
    phase: 'Analyze',
    modules: [
      { name: '假设检验', desc: 't检验·ANOVA·非参数' },
      { name: '回归分析', desc: '线性·岭·Lasso·随机森林' },
      { name: '多变量', desc: 'PCA·聚类·判别·因子' },
      { name: '可靠性', desc: 'Weibull·寿命·MTBF' },
      { name: '非参数', desc: 'Mann-Whitney·Kruskal-Wallis' },
      { name: '卡方', desc: '独立性·拟合优度·列联表' },
    ],
  },
  {
    phase: 'Improve / Control',
    modules: [
      { name: 'DOE', desc: '全因子·部分因子·RSM·田口' },
      { name: 'SPC', desc: 'I-MR·Xbar-R·P·C·EWMA' },
      { name: '时间序列', desc: 'ARIMA·指数平滑·趋势分解' },
    ],
  },
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [sampleLoading, setSampleLoading] = useState('');

  const loadSample = async (sampleName: string, target: 'analysis' | 'chart') => {
    setSampleLoading(sampleName);
    try {
      const td = await fetchTestData(sampleName);
      const path = target === 'analysis' ? '/analysis/workspace' : '/smart-chart/workspace';
      navigate(`${path}?fileId=${td.file_id}&fileName=${td.filename}`);
    } catch (e: any) {
      message.error(`示例数据加载失败: ${e?.message || '未知错误'}`);
    } finally {
      setSampleLoading('');
    }
  };

  const cardBg = isDark ? 'var(--card-bg)' : '#fff';
  const borderCol = isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-color)';

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 24px' }}>

      {/* ===== HERO: 非对称，左文右产品预览 ===== */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1.1fr',
        gap: 48, alignItems: 'center',
        padding: '64px 0 56px', minHeight: 420,
      }}>
        {/* 左：文案 */}
        <div>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: 'var(--primary)', marginBottom: 20,
          }}>
            Industrial Data Analytics
          </div>
          <h1 style={{
            fontSize: 42, fontWeight: 700, lineHeight: 1.15,
            letterSpacing: -1.2,
            color: 'var(--title-color)', margin: '0 0 16px',
          }}>
            用数据驱动<br />质量改善决策
          </h1>
          <p style={{
            fontSize: 16, lineHeight: 1.7,
            color: 'var(--text-secondary)', margin: '0 0 32px',
            maxWidth: 400,
          }}>
            12 个统计分析模块覆盖 DMAIC 全流程。上传数据，AI 自动推荐分析方法，代码驱动确保每一个数值可审计。
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/analysis/workspace')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', fontSize: 14, fontWeight: 600,
                background: 'var(--primary)', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(83,58,253,0.3)',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}
            >
              <UploadOutlined /> 上传数据分析
            </button>
            <button
              onClick={() => navigate('/templates')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', fontSize: 14, fontWeight: 500,
                background: isDark ? 'rgba(255,255,255,0.06)' : 'transparent',
                color: 'var(--text-primary)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : 'var(--border-color)'}`,
                borderRadius: 6, cursor: 'pointer',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}
            >
              <FileAddOutlined /> 生成数据模板
            </button>
          </div>
          {/* Sample data quick entry */}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: '28px' }}>示例数据：</span>
            <button
              onClick={() => loadSample('sample_manufacturing', 'analysis')}
              disabled={!!sampleLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 12px', fontSize: 12, fontWeight: 500,
                background: isDark ? 'rgba(83,58,253,0.1)' : 'rgba(83,58,253,0.06)',
                color: 'var(--primary)', border: `1px solid ${isDark ? 'rgba(83,58,253,0.3)' : 'rgba(83,58,253,0.2)'}`,
                borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <ExperimentOutlined /> {sampleLoading === 'sample_manufacturing' ? '加载中...' : '制造业质量数据 → 统计分析'}
            </button>
            <button
              onClick={() => loadSample('sample_sales', 'chart')}
              disabled={!!sampleLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 12px', fontSize: 12, fontWeight: 500,
                background: isDark ? 'rgba(21,190,83,0.1)' : 'rgba(21,190,83,0.06)',
                color: '#15be53', border: `1px solid ${isDark ? 'rgba(21,190,83,0.3)' : 'rgba(21,190,83,0.2)'}`,
                borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <ExperimentOutlined /> {sampleLoading === 'sample_sales' ? '加载中...' : '销售时序数据 → 智能图表'}
            </button>
          </div>
        </div>

        {/* 右：产品预览（模拟 dashboard 截图） */}
        <div style={{
          background: isDark ? 'var(--brand-dark)' : 'var(--navy)',
          borderRadius: 12, padding: '24px 24px 0', overflow: 'hidden',
          boxShadow: 'var(--shadow-lg)',
          position: 'relative',
        }}>
          {/* 模拟窗口标题栏 */}
          <div style={{
            display: 'flex', gap: 6, marginBottom: 16,
          }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
          </div>
          {/* 模拟 dashboard 内容 */}
          <div style={{
            background: isDark ? '#1a2035' : '#f6f8fa',
            borderRadius: '8px 8px 0 0', padding: '20px 20px 0',
            minHeight: 280,
          }}>
            {/* KPI row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
              {[
                { label: 'Cpk', value: '1.45', color: '#15be53' },
                { label: 'Sigma', value: '4.35', color: 'var(--primary)' },
                { label: 'PPM', value: '2,700', color: '#e6a200' },
              ].map(k => (
                <div key={k.label} style={{
                  background: cardBg, borderRadius: 6, padding: '12px 10px',
                  border: `1px solid ${borderCol}`,
                }}>
                  <div style={{
                    fontSize: 9, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: 0.8,
                    marginBottom: 4, fontWeight: 600,
                  }}>{k.label}</div>
                  <div style={{
                    fontSize: 18, fontWeight: 700, color: k.color,
                    fontFamily: 'var(--font-mono)', fontFeatureSettings: '"tnum"',
                  }}>{k.value}</div>
                </div>
              ))}
            </div>
            {/* Chart placeholder bars */}
            <div style={{
              background: cardBg, borderRadius: '8px 8px 0 0',
              border: `1px solid ${borderCol}`, borderBottom: 'none',
              padding: 16, minHeight: 160,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-primary)',
                marginBottom: 12, paddingLeft: 10,
                borderLeft: '3px solid var(--primary)',
              }}>
                过程能力直方图
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100, padding: '0 8px' }}>
                {[18, 35, 62, 85, 100, 88, 65, 40, 22, 10].map((h, i) => (
                  <div key={i} style={{
                    flex: 1, height: `${h}%`,
                    background: `rgba(83,58,253,${0.3 + h / 200})`,
                    borderRadius: '3px 3px 0 0',
                    transition: 'height 0.5s ease',
                  }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== BENTO GRID: 非等分功能区 ===== */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 1fr',
        gridTemplateRows: 'auto auto',
        gap: 16, marginBottom: 48,
      }}>
        {/* 主卡：统计分析（大） */}
        <div
          onClick={() => navigate('/analysis/workspace')}
          className="card-trendata"
          style={{
            padding: '32px 28px', cursor: 'pointer',
            gridRow: 'span 2',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 9999, marginBottom: 16,
              background: 'rgba(83,58,253,0.08)', color: 'var(--primary)',
              fontSize: 11, fontWeight: 600,
            }}>
              核心功能
            </div>
            <h2 style={{
              fontSize: 24, fontWeight: 700, color: 'var(--text-primary)',
              margin: '0 0 10px', lineHeight: 1.3,
            }}>
              统计分析
            </h2>
            <p style={{
              fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7,
              margin: '0 0 24px',
            }}>
              12 个专业模块覆盖 DMAIC 全流程。代码优先架构确保每一个 Cpk、p 值、R² 都可追溯。AI 自动解读结果，给出改善建议。
            </p>
          </div>
          {/* Mini module grid */}
          <div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20,
            }}>
              {['基本统计', 'Cpk', 'SPC', 'ANOVA', 'GR&R', '回归', 'DOE', 'Weibull'].map(m => (
                <span key={m} style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 4,
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--bg-subtle)',
                  color: 'var(--text-secondary)',
                  border: `1px solid ${borderCol}`,
                }}>
                  {m}
                </span>
              ))}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--primary)', fontSize: 14, fontWeight: 600,
            }}>
              上传数据开始分析 <ArrowRightOutlined style={{ fontSize: 12 }} />
            </div>
          </div>
        </div>

        {/* 右上：智能图表（小） */}
        <div
          onClick={() => navigate('/smart-chart/workspace')}
          className="card-trendata"
          style={{ padding: '24px', cursor: 'pointer' }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(21,190,83,0.1)', color: '#15be53',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, marginBottom: 14,
          }}>
            <LineChartOutlined />
          </div>
          <h3 style={{
            fontSize: 16, fontWeight: 600, color: 'var(--text-primary)',
            margin: '0 0 6px',
          }}>
            智能图表
          </h3>
          <p style={{
            fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
            margin: '0 0 14px',
          }}>
            42 种交互式可视化，AI 推荐最佳图表类型
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            color: '#15be53', fontSize: 13, fontWeight: 600,
          }}>
            创建图表 <ArrowRightOutlined style={{ fontSize: 11 }} />
          </div>
        </div>

        {/* 右下：数据模板（小） */}
        <div
          onClick={() => navigate('/templates')}
          className="card-trendata"
          style={{ padding: '24px', cursor: 'pointer' }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(0,115,230,0.1)', color: '#0073e6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, marginBottom: 14,
          }}>
            <FileAddOutlined />
          </div>
          <h3 style={{
            fontSize: 16, fontWeight: 600, color: 'var(--text-primary)',
            margin: '0 0 6px',
          }}>
            数据模板
          </h3>
          <p style={{
            fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
            margin: '0 0 14px',
          }}>
            标准化 MSA、DOE、SPC 数据采集流程
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            color: '#0073e6', fontSize: 13, fontWeight: 600,
          }}>
            浏览模板 <ArrowRightOutlined style={{ fontSize: 11 }} />
          </div>
        </div>
      </div>

      {/* ===== DMAIC 模块分组（非平铺） ===== */}
      <div style={{ marginBottom: 48 }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{
            fontSize: 20, fontWeight: 700, color: 'var(--title-color)',
            margin: '0 0 6px',
          }}>
            分析模块
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            按六西格玛 DMAIC 阶段组织，点击模块直接进入分析
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {dmaic.map(group => (
            <div key={group.phase} style={{
              background: cardBg,
              border: `1px solid ${borderCol}`,
              borderRadius: 8, overflow: 'hidden',
            }}>
              {/* Phase header */}
              <div style={{
                padding: '10px 20px',
                background: isDark ? 'rgba(83,58,253,0.08)' : 'rgba(83,58,253,0.03)',
                borderBottom: `1px solid ${borderCol}`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--primary)',
                  textTransform: 'uppercase', letterSpacing: 1,
                }}>
                  {group.phase}
                </span>
                <span style={{
                  fontSize: 11, color: 'var(--text-muted)',
                }}>
                  {group.modules.length} 个模块
                </span>
              </div>
              {/* Modules row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(group.modules.length, 6)}, 1fr)`,
                gap: 0,
              }}>
                {group.modules.map((mod, i) => (
                  <div
                    key={mod.name}
                    onClick={() => navigate('/analysis/workspace')}
                    style={{
                      padding: '14px 18px', cursor: 'pointer',
                      borderRight: i < group.modules.length - 1 ? `1px solid ${borderCol}` : 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = isDark ? 'rgba(83,58,253,0.08)' : 'rgba(83,58,253,0.03)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                      marginBottom: 3,
                    }}>{mod.name}</div>
                    <div style={{
                      fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4,
                    }}>{mod.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== Footer ===== */}
      <div style={{
        textAlign: 'center', padding: '20px 0 40px',
        fontSize: 12, color: 'var(--text-muted)',
        borderTop: `1px solid ${borderCol}`,
      }}>
        trendata v3.0 · 三层控制面架构 · 代码优先 · Stripe Design
      </div>
    </div>
  );
};

export default Home;
